import React from "react";
import {Audit} from "../interfaces/AuditInterface";
import ActionButton from "./ActionButton";

interface Employee {
  firstName: string;
  middleName: string | null;
  lastName: string;
  altEmployeeID: string;
}

interface AuditDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditData: Audit[]; // now an array
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({ isOpen, onClose, auditData }) => {
  if (!isOpen || !auditData || auditData.length === 0) return null;

  // Audit stamps are UTC instants (the server writes them through a UTC-mode
  // driver), so render them in the viewer's own zone rather than a hardcoded
  // one. A value that arrives without a zone marker is still UTC by contract —
  // tag it, or Date.parse would read the digits as local wall clock.
  const formatDateTime = (isoString: string) => {
    const text = (isoString ?? "").trim();
    const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(text) ? text : `${text}Z`);

    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };  

  const getFullName = (employee: Employee) => {
    return [employee.firstName, employee.middleName, employee.lastName].filter(Boolean).join(" ");
  };

  return (
    <div className="audit-modal-overlay">
      <div className="audit-modal">
        <div className="audit-modal-header">
          <h2>Audit Log</h2>
        </div>
        <div className="audit-modal-content">
            <div className="audit-table-wrapper">
                <table>
                <thead>
                    <tr>
                    <th>Date / Time</th>
                    <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {[...auditData]
                    .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
                    .map((entry) => (
                        <tr key={entry.auditID}>
                        <td>{formatDateTime(entry.dateTime)}</td>
                        <td>{entry.description}</td>
                        </tr>
                    ))}
                </tbody>
                </table>
            </div>
        </div>
        <div className="audit-modal-actions">
          <ActionButton title="Close" onAction={onClose} isEnabled={true}/>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailModal;

// Add CSS styles directly in this file using a template literal

const styles = `
.audit-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.audit-modal {
    background: white;
    border-radius: 12px;
    width: 80%;
    max-width: 90%;
    max-height: 80%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    animation: auditFadeIn 0.3s ease-in-out;
}

@keyframes auditFadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.audit-modal-header {
    background-color: #2f80ed;
    padding: 20px;
    color: white;
    text-align: center;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.audit-modal-header h2 {
    margin: 0;
    font-size: 22px;
}

.audit-modal-content {
    padding: 20px;
    overflow-y: auto;
}

.audit-modal-content table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    color: #333;
}

.audit-modal-content th,
.audit-modal-content td {
    padding: 12px;
    border: 1px solid #ddd;
    text-align: left;
    vertical-align: top;
}

.audit-modal-content th {
    background-color: #f0f0f0;
    font-weight: 600;
}

.audit-modal-content td {
    background-color: #fafafa;
}

.audit-modal-content th:nth-child(2),
.audit-modal-content td:nth-child(2) {
    width: 81%; /* or adjust to whatever fits best */
    word-wrap: break-word;
}

.audit-table-wrapper {
    max-height: 300px; /* adjust as needed */
    overflow-y: auto;
    border: 1px solid #ccc;
    scrollbar-width: thin;
    overflow-x: wrap;
}

.audit-modal-actions {
    display: flex;
    justify-content: center;
    padding: 10px;
    gap: 10px;
}
`;

// Inject styles into the page
const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);